import React, { useState } from "react";
import { Box } from "@mui/material";
import dayjs from "dayjs";
import LGModalCrud from "../LGCrud/LGModalCrud";
import LGModal from "../../molecules/LGModal";
import {
  deletePaymentTx,
  exportPaymentTxLayout,
  exportPaymentTxLayoutSelection,
  exportPaymentTxPDF,
  getPaymentTxList,
} from "../../api/paymentTx";
import { currencyFormatterMXN } from "../../services/valueFormatters";
import { handlePrint as printPDF, handleDownload as downloadPDF } from "../../services/PDFHandler.ts";
import { useSnack } from "../../services/SnakbarProvider";
import { handleError } from "../../services/errorHandler";
import PaymentTxForm from "./PaymentTxForm";
import PaymentTxAllocationsView from "./PaymentTxAllocationsView";
import PaymentTxExportLayoutForm from "./PaymentTxExportLayoutForm";
import DateFilterModal from "../ReceiptAcknowledgement/DateFilterModal";

const getCurrentYearMonthCode = () => {
  const now = new Date();
  const year = now.getFullYear().toString().slice(-2);
  const month = (now.getMonth() + 1).toString().padStart(2, "0");
  return `${year}${month}`;
};

export default function PaymentTxCrud({ props }) {
  const { companyId } = props;
  const { openSnack } = useSnack();
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [dateFilterOpen, setDateFilterOpen] = useState(false);
  const [dateCode, setDateCode] = useState(getCurrentYearMonthCode);

  const handleDateFilter = (date) => {
    if (!date) {
      setDateCode(getCurrentYearMonthCode());
      return;
    }
    const year = date.year().toString().slice(-2);
    const month = (date.month() + 1).toString().padStart(2, "0");
    setDateCode(`${year}${month}`);
  };

  const handleFileDownload = async (id) => {
    try {
      const response = await exportPaymentTxPDF(id);
      downloadPDF(
        response,
        "pago_tx",
        "No se pudo generar el PDF",
        () => {
          openSnack("No se pudo generar el PDF", "error");
        }
      );
    } catch (e) {
      openSnack("No se pudo generar el PDF", "error");
    }
  };

  const handlePrint = async (id) => {
    try {
      const response = await exportPaymentTxPDF(id);
      printPDF(response, "No se pudo generar el PDF", () => {
        openSnack("No se pudo generar el PDF", "error");
      });
    } catch (e) {
      openSnack("No se pudo generar el PDF", "error");
    }
  };

  const downloadLayout = (layout, fileName) => {
    const url = window.URL.createObjectURL(new Blob([layout.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const handleExportLayout = async (paymentDate) => {
    try {
      const layout = await exportPaymentTxLayout(companyId, paymentDate.format("YYYY-MM-DD"));
      downloadLayout(layout, `poliza_egreso_${paymentDate.format("YYYYMMDD")}.txt`);
      setExportModalOpen(false);
    } catch (error) {
      openSnack(handleError(error) || "Error al exportar layout", "error");
    }
  };

  const handleExportLayoutSelection = async (selectedRows) => {
    if (selectedRows.length === 0) {
      openSnack("Selecciona al menos una transacción", "warning");
      return;
    }
    try {
      const layout = await exportPaymentTxLayoutSelection(selectedRows);
      downloadLayout(layout, `poliza_egreso_${dayjs().format("YYYYMMDD_HHmmss")}.txt`);
    } catch (error) {
      openSnack(handleError(error) || "Error al exportar layout", "error");
    }
  };

  return (
    <Box width={"100%"}>
      <DateFilterModal
        open={dateFilterOpen}
        handleClose={() => setDateFilterOpen(false)}
        onSubmit={handleDateFilter}
      />
      <LGModal
        open={exportModalOpen}
        handleClose={() => setExportModalOpen(false)}
        header="Exportar Layout"
      >
        <PaymentTxExportLayoutForm onExport={handleExportLayout} />
      </LGModal>
      <LGModalCrud
        props={{
          actions: ["create", "update", "delete", "detail", "print", "download", "select"],
          data: {
            companyId,
            sortField: "code",
            sortDir: "desc",
            codePrefix: dateCode,
          },
          fetchFunction: getPaymentTxList,
          deleteFunction: deletePaymentTx,
          printFunction: handlePrint,
          downloadFunction: handleFileDownload,
          customButtons: [
            {
              label: "Filtrar por Mes/Año",
              icon: "calendar_month",
              color: "primary",
              onClick: () => setDateFilterOpen(true),
            },
            {
              label: "Exportar Layout por Fecha",
              icon: "event",
              color: "secondary",
              onClick: () => setExportModalOpen(true),
            },
            {
              label: "Exportar Layout",
              icon: "file_download",
              color: "secondary",
              onClick: handleExportLayoutSelection,
              showOnlyWithSelection: true,
            },
          ],
          detailMenu: [
            {
              label: "Ver aplicaciones",
              header: "Aplicaciones",
              body: <PaymentTxAllocationsView />,
            },
          ],
          columns: [
            {
              field: "code",
              headerName: "Folio",
              flex: 0.12,
              filterable: true,
              valueGetter: (value, row) => row.code || row.folio || "",
            },
            {
              field: "paymentDate",
              headerName: "Fecha de Pago",
              flex: 0.15,
              type: "date",
              filterable: true,
              valueGetter: (value, row) =>
                row?.paymentDate ? dayjs(row.paymentDate).toDate() : null,
              valueFormatter: (value) =>
                value != null ? dayjs(value).format("DD/MM/YYYY") : "",
            },
            {
              field: "provider",
              headerName: "Proveedor",
              flex: 0.15,
              filterable: true,
              valueGetter: (value, row) => (row.provider ? `${row.provider.code}` : ""),
            },
            {
              field: "currency",
              headerName: "Moneda",
              flex: 0.1,
              filterable: true,
              valueGetter: (value, row) => (row.currency ? `${row.currency.symbol}` : ""),
            },
            {
              field: "amountPaid",
              headerName: "Monto Pagado",
              flex: 0.15,
              type: "number",
              filterable: true,
              ...currencyFormatterMXN,
            },
            {
              field: "sourceBankAccount",
              headerName: "Cuenta de Origen",
              flex: 0.15,
              filterable: true,
              valueGetter: (value, row) => row.sourceBankAccount?.name || "",
            },
            { field: "bankReference", headerName: "Referencia", flex: 0.15, filterable: true },
            { field: "trackingKey", headerName: "Clave de Rastreo", flex: 0.15, filterable: true },
            {
              field: "scheduledPaymentId",
              headerName: "Corrida",
              flex: 0.1,
              type: "number",
              filterable: true,
              valueGetter: (value, row) => row.scheduledPaymentId || "",
            },
          ],
          addModalData: {
            header: "Nueva Transacción de Pago",
            body: <PaymentTxForm companyId={companyId} />,
          },
          editModalData: {
            header: "Editar Transacción de Pago",
            body: <PaymentTxForm companyId={companyId} />,
          },
          dataGridProps: {
            initialState: {
              sorting: {
                sortModel: [{ field: "code", sort: "desc" }],
              },
            },
          },
        }}
      />
    </Box>
  );
}
